
import React, { useState } from 'react';
import FormDialog from '../../../Components/Modal/modal';
import {
  CANCEL_TEXT,
  CERTIFICATION_CATEGORY,
  SAVE,
  ADD_EDIT_RESPONSE_MESSAGES,
  AUTHENTICATION_FAILED,
} from '../../../Constants/strings';
import CommonTextField from '../../../Components/TextField/textField';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import './addCategory.scss';
import PropTypes from 'prop-types';
import UseAxios from '../../../Api/http/http';
import { addCategory } from '../../../Api/request/certificationCategoryMaster';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';


const AddCategoryForm = ({
  openAddModal,
  handleClose,
  lastUpdatedBy,
  updateCategoriesList,
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [subCategories, setSubCategories] = useState(['']);
  const [categoryError, setCategoryError] = useState('');
  const [subCategoryErrors, setSubCategoryErrors] = useState([]);
  const [autoFocus, setAutoFocus] = useState(false);
  const { snackbarOpen } = useSnackbar();

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const resetValues = () => {
    setCategoryError('');
    setSubCategoryErrors([]);
    setCategoryName('');
    setSubCategories(['']);
  };

  const handleAddSubCategory = () => {
    setSubCategories([...subCategories, '']);
    setSubCategoryErrors([...subCategoryErrors, '']);
    setAutoFocus(false);
  };

  const handleRemoveSubCategory = (index) => {
    const updatedSubCategories = [...subCategories];
    const updatedSubCategoryErrors = [...subCategoryErrors];
    updatedSubCategories.splice(index, 1);
    updatedSubCategoryErrors.splice(index, 1);
    setSubCategories(updatedSubCategories);
    setSubCategoryErrors(updatedSubCategoryErrors);
  };

  const handleSubCategoryChange = (index, value) => {
    const updatedSubCategories = [...subCategories];
    updatedSubCategories[index] = value;
    setSubCategories(updatedSubCategories);
    validateSubCategory(index, value);
  };

  const handleInputChange = (value) => {
    setCategoryName(value);
    validateCategory(value);
    setAutoFocus(false);
  };

  const validateCategory = (value) => {
    const specialCharPattern = /[^a-zA-Z0-9 ]/;
    if (specialCharPattern.test(value)) {
      setCategoryError(ADD_EDIT_RESPONSE_MESSAGES.PERFORM_CYCLE_NO_SPECIAL_CHAR);
    } else if (value.length < 3 || value.length > 32) {
      setCategoryError(CERTIFICATION_CATEGORY.NAME_MUST_BE);
    } else {
      setCategoryError('');
    }
  };

  const validateSubCategory = (index, value) => {
    const specialCharPattern = /[^a-zA-Z0-9 ]/;
    const updatedSubCategoryErrors = [...subCategoryErrors];
    if (specialCharPattern.test(value)) {
      updatedSubCategoryErrors[index] = ADD_EDIT_RESPONSE_MESSAGES.PERFORM_CYCLE_NO_SPECIAL_CHAR;
    } else if (value.length < 3 || value.length > 32) {
      updatedSubCategoryErrors[index] = CERTIFICATION_CATEGORY.NAME_MUST_BE;
    } else if (subCategories.includes(value) && subCategories.indexOf(value) !== index) {
      updatedSubCategoryErrors[index] = CERTIFICATION_CATEGORY.SUBCATEGORY_UNIQUE;
    } else {
      updatedSubCategoryErrors[index] = '';
    }
    setSubCategoryErrors(updatedSubCategoryErrors);
  };

  const handleSubmit = async () => {
    const trimmedCategoryName = categoryName.trim();
    const trimmedSubCategories = subCategories.map(subCategory => subCategory.trim());

    if (categoryError !== '' || subCategoryErrors.some(error => error !== '')) {
      return;
    }

    const payload = {
      categoryName: trimmedCategoryName,
      subCategories: trimmedSubCategories,
      lastUpdatedBy: lastUpdatedBy,
    };

    if (!payload.categoryName || payload.subCategories.some(subCategory => !subCategory)) {
      setCategoryError(!payload.categoryName ? CERTIFICATION_CATEGORY.REQUIRED_FILED : '');
      setSubCategoryErrors(payload.subCategories.map(subCategory =>
        !subCategory ? CERTIFICATION_CATEGORY.REQUIRED_FILED : ''));
      return;
    }

    try {
      const response = await UseAxios(addCategory(payload));
      handleResponse(response);
      setAutoFocus(false);
    } catch (error) {
      snackbarOpen(ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN, 'warning');
      handleCloseWithReset();
      console.error('Error adding category', error);
    }
  };

  const handleResponse = (response) => {
    switch (response.data.status || response.data.response.data.statusCode) {
    case 201:
      snackbarOpen(response.data.message, 'success');
      updateCategoriesList();
      handleCloseWithReset();
      break;
    case 400:
      snackbarOpen(response.data.response.data.externalMessage, 'warning');
      break;
    case 401:
      snackbarOpen(AUTHENTICATION_FAILED, 'warning');
      break;
    default:
      snackbarOpen(ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN, 'warning');
      handleCloseWithReset();
      break;
    }
  };

  return (
    <div>
      <FormDialog
        title={CERTIFICATION_CATEGORY.ADD_BUTTON_TEXT}
        open={openAddModal}
        button1Action={handleCloseWithReset}
        dialogPaperCss="add-cert-category-dialog-paper"
        dialogCss="certificate-category-dialog"
        dialogBodycontent={
          <div className="add-cert-category-dialog-body">
            <div className="category-field">
              <CommonTextField
                textCss="category-field-name"
                label={CERTIFICATION_CATEGORY.CATEGORY}
                value={categoryName}
                onChange={(event) => handleInputChange(event.target.value)}
                placeholder={CERTIFICATION_CATEGORY.ENTER_CATEGORY}
                autoFocus={true}
                size="small"
                id={'adding-category'}
              />
              {categoryError && (
                <div className="add-cert-category-error">
                  <small>{categoryError}</small>
                </div>
              )}
            </div>
            {subCategories.map((subCategory, index) => (
              <div key={index + 1}>
                <CommonTextField
                  textCss="sub-cat-field"
                  label={
                    <span className="label-span">
                      {index == 0 && CERTIFICATION_CATEGORY.ADD_SUB_CATEGORY}
                      {index == 0 && subCategories.length > 1 && (
                        <span className="sub-field-icon">
                          {' '}
                          <AddIcon
                            sx={{ fontSize: 18 }}
                            className="sub-add-icon"
                          />
                        </span>
                      )}
                    </span>
                  }
                  value={subCategory}
                  onChange={(event) =>
                    handleSubCategoryChange(index, event.target.value)
                  }
                  placeholder={CERTIFICATION_CATEGORY.ENTER_SUB_CATEGORY}
                  autoFocus={autoFocus}
                  size="small"
                  id={'adding-sub-category'}
                />
                {index === 0 && (
                  <span className="sub-cat-side-icon">
                    <AddIcon onClick={handleAddSubCategory} />
                  </span>
                )}
                {index !== 0 && subCategories.length >= 2 && (
                  <span className="sub-cat-remove-icon">
                    <RemoveIcon
                      onClick={() => handleRemoveSubCategory(index)}
                    />
                  </span>
                )}
                {subCategoryErrors[index] && (
                  <div className="add-cert-category-error">
                    <small>{subCategoryErrors[index]}</small>
                  </div>
                )}
              </div>
            ))}
          </div>
        }
        button1={CANCEL_TEXT}
        button2={SAVE}
        button2Action={handleSubmit}
        cancelButtonId={'add-category-cancel-btn'}
        submitButtonId={'add-category-submit-btn'}
      />
    </div>
  );
};

AddCategoryForm.propTypes = {
  openAddModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  lastUpdatedBy: PropTypes.string.isRequired,
  updateCategoriesList: PropTypes.func.isRequired,
};

export default AddCategoryForm;
